/**
 * Bridges the token layer into plain CSS.
 *
 * Most of the app styles through react-jss or the antd theme, both of which
 * read the TypeScript tokens directly. But a handful of rules have to live in
 * real stylesheets — the antd notification safe-area fix in `App.css` is the
 * standing example, because it targets a portal that renders outside our React
 * tree. Rather than let those files re-hardcode hex values, this publishes the
 * tokens as `--gg-*` custom properties on `:root` at boot.
 *
 * Deliberately narrow: only the tokens a stylesheet actually needs are
 * published. This is an escape hatch, not a second API — if you find yourself
 * wanting to add a token here, check first whether the rule belongs in a
 * component instead.
 */

import { colors } from './semantic';
import { fontFamily, fontWeight, radius, space } from './tokens';

const cssVariables: Record<string, string> = {
  '--gg-color-surface-page': colors.surface.page,
  '--gg-color-surface-base': colors.surface.base,
  '--gg-color-surface-sunken': colors.surface.sunken,
  '--gg-color-surface-inverse': colors.surface.inverse,
  '--gg-color-text-primary': colors.text.primary,
  '--gg-color-text-secondary': colors.text.secondary,
  '--gg-color-text-inverse': colors.text.inverse,
  '--gg-color-border-subtle': colors.border.subtle,
  '--gg-color-brand-primary': colors.brand.primary,
  '--gg-color-brand-accent': colors.brand.accent,

  '--gg-font-display': fontFamily.display,
  '--gg-font-text': fontFamily.text,
  '--gg-font-mono': fontFamily.mono,
  '--gg-font-weight-regular': `${fontWeight.regular}`,
  '--gg-font-weight-medium': `${fontWeight.medium}`,

  '--gg-radius-md': `${radius.md}px`,
  '--gg-radius-lg': `${radius.lg}px`,

  '--gg-space-md': `${space.md}px`,
  '--gg-space-lg': `${space.lg}px`,
};

/**
 * Writes the token custom properties onto the document root.
 *
 * Call once, as early as possible — before React renders, so the first paint
 * already has them. No-ops outside a DOM (SSR, tests running in node).
 */
export function applyCssVariables(): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  Object.entries(cssVariables).forEach(([name, value]) => {
    root.style.setProperty(name, value);
  });
}

export default cssVariables;
